import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import Button from "./Button";

/**
 * Generic multi-select dropdown with a search box, "Select all" and "Clear" actions.
 * options: [{ value, label }]
 * value: string[]
 * onChange: (next: string[]) => void
 *
 * maxSelections (optional number): when provided, unselected options are
 * disabled once the limit is reached, and "Select all" is also disabled.
 * Currently used by MonthlyClinicSelection to cap clinic selection at 1
 * when multiple months are chosen.
 */
export default function MultiSelectDropdown({
  options = [],
  value = [],
  onChange,
  placeholder = "Select…",
  className = "",
  id: idProp,
  ariaLabel,
  emptyLabel = "All",
  maxLabelCount = 2,
  searchable = true,
  // Optional upper bound on simultaneous selections. When selectedSet.size
  // reaches this number, unchecked items are greyed out and non-interactive.
  maxSelections,
}) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    function onDocClick(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const selectedSet = useMemo(() => new Set(value || []), [value]);
  const labelByValue = useMemo(() => {
    const m = new Map();
    options.forEach((o) => m.set(o.value, o.label));
    return m;
  }, [options]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => String(o.label).toLowerCase().includes(q) || String(o.value).toLowerCase().includes(q),
    );
  }, [options, query]);

  const triggerLabel = useMemo(() => {
    if (selectedSet.size === 0) return emptyLabel;
    const selectedArr = (value || []).filter((v) => selectedSet.has(v));
    if (selectedArr.length <= maxLabelCount) {
      return selectedArr.map((v) => labelByValue.get(v) ?? v).join(", ");
    }
    return `${selectedArr.length} selected`;
  }, [emptyLabel, labelByValue, maxLabelCount, selectedSet, value]);

  const toggle = useCallback(
    (v) => {
      const next = new Set(selectedSet);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      onChange?.(Array.from(next));
    },
    [onChange, selectedSet],
  );

  const selectAllVisible = useCallback(() => {
    const next = new Set(selectedSet);
    filteredOptions.forEach((o) => next.add(o.value));
    onChange?.(Array.from(next));
  }, [filteredOptions, onChange, selectedSet]);

  const clear = useCallback(() => onChange?.([]), [onChange]);

  return (
    <div className={`relative w-full ${className}`} ref={wrapRef}>
      <Button
        id={id}
        type="button"
        variant="outline"
        size="md"
        onClick={() => setOpen((v) => !v)}
        aria-label={ariaLabel}
        className="mt-0 w-full max-w-[280px] justify-between bg-white"
      >
        <span className="font-medium truncate text-left flex-1">
          {selectedSet.size === 0 ? placeholder : triggerLabel}
        </span>
        <span className="text-palette-gray-secondary ml-2">▾</span>
      </Button>

      {open && (
        <div className="absolute z-50 mt-2 w-[300px] rounded-2xl border border-palette-gray-divider bg-white p-3 shadow-xl">
          {searchable && (
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full rounded-lg border border-palette-gray-divider px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-palette-indigo-label mb-2"
            />
          )}

          <div className="flex items-center justify-between text-xs mb-2">
            <button
              type="button"
              onClick={selectAllVisible}
              className="text-palette-indigo-badge hover:text-palette-indigo-text disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={
                filteredOptions.length === 0 ||
                // Also disable "Select all" when the cap is already reached.
                (maxSelections != null && selectedSet.size >= maxSelections)
              }
            >
              Select all{query ? " (filtered)" : ""}
            </button>
            <button
              type="button"
              onClick={clear}
              className="text-palette-gray-secondary hover:text-palette-gray-heading"
              disabled={selectedSet.size === 0}
            >
              Clear
            </button>
          </div>

          <div className="max-h-60 overflow-y-auto -mx-1 px-1">
            {filteredOptions.length === 0 ? (
              <div className="text-sm text-palette-gray-secondary px-2 py-3">No matches.</div>
            ) : (
              filteredOptions.map((o) => {
                const checked = selectedSet.has(o.value);
                // Disable unchecked items when the selection cap has been reached.
                // Already-checked items stay interactive so the user can deselect.
                const itemDisabled =
                  !checked && maxSelections != null && selectedSet.size >= maxSelections;
                return (
                  <label
                    key={o.value}
                    className={[
                      "flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm",
                      itemDisabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer",
                      checked
                        ? "bg-palette-indigo-soft"
                        : !itemDisabled
                          ? "hover:bg-palette-gray-soft"
                          : "",
                    ].join(" ")}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={checked}
                      disabled={itemDisabled}
                      onChange={() => toggle(o.value)}
                    />
                    <span className="truncate">{o.label}</span>
                  </label>
                );
              })
            )}
          </div>

          {/* Hint shown when the selection cap is active and already reached. */}
          {maxSelections != null && selectedSet.size >= maxSelections && (
            <div className="mt-2 text-[11px] text-palette-gray-secondary">
              Max {maxSelections} {maxSelections === 1 ? "clinic" : "clinics"} allowed when multiple
              months are selected.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
